import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  approveWithdrawal,
  fetchOpsBoard,
  fetchReadOnly,
  fetchWithdrawalQueue,
  rejectWithdrawal,
  setReadOnly,
} from '@/lib/api'

/**
 * Query keys, named once.
 *
 * The kill switch and the ops board both report `readOnly`, so flipping the switch has
 * to invalidate both or the board keeps showing the old state until its next poll —
 * two parts of the same screen disagreeing about whether the protocol is live.
 */
export const qk = {
  ops: ['admin', 'ops'] as const,
  readOnly: ['admin', 'readonly'] as const,
  withdrawals: ['admin', 'withdrawals'] as const,
}

/**
 * The operations board.
 *
 * Polled every 15 seconds. The backend's snapshot worker writes on a 5-second loop, so
 * polling faster only costs rate limit without ever showing a newer number; polling
 * much slower means an operator watching an incident is reading stale figures.
 */
export function useOpsBoard(enabled: boolean) {
  return useQuery({
    queryKey: qk.ops,
    queryFn: fetchOpsBoard,
    enabled,
    refetchInterval: 15_000,
    // Keep the previous board on screen while the next one loads, so a poll does not
    // blank the page an operator is reading mid-incident.
    placeholderData: (prev) => prev,
  })
}

/**
 * The kill-switch state on its own.
 *
 * Its endpoint is not admin-gated, so this resolves before sign-in — which is what
 * lets the login screen already tell an operator whether the protocol is halted.
 */
export function useReadOnlyState() {
  return useQuery({
    queryKey: qk.readOnly,
    queryFn: fetchReadOnly,
    refetchInterval: 15_000,
  })
}

export function useSetReadOnly() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ enabled, reason }: { enabled: boolean; reason: string }) =>
      setReadOnly(enabled, reason),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.readOnly })
      void queryClient.invalidateQueries({ queryKey: qk.ops })
    },
  })
}

/**
 * The withdrawal approval queue.
 *
 * Polled on a slower cadence than the ops board: rows enter it at human speed, and a
 * table that reorders under the cursor while someone is deciding on a row is a way to
 * approve the wrong payment.
 */
export function useWithdrawalQueue(enabled: boolean) {
  return useQuery({
    queryKey: qk.withdrawals,
    queryFn: fetchWithdrawalQueue,
    enabled,
    refetchInterval: 30_000,
    placeholderData: (prev) => prev,
  })
}

/**
 * Approve and reject are separate mutations rather than one parameterised call.
 *
 * They are not two settings of the same action: one releases money to the payout
 * executor, the other closes the request with an explanation the user reads. Keeping
 * them apart means a component cannot accidentally pass the wrong flag, and each gets
 * the confirmation treatment it deserves at the call site.
 */
export function useApproveWithdrawal() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => approveWithdrawal(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.withdrawals }),
  })
}

export function useRejectWithdrawal() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => rejectWithdrawal(id, reason),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.withdrawals }),
  })
}
